import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { GoogleMap, useJsApiLoader, OverlayView, Polyline, Marker } from '@react-google-maps/api';
import { useAuth } from '../../hooks/useAuth';
import { useWells } from '../../hooks/useWells';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Plane, 
  Layers, 
  Activity, 
  Clock, 
  Map as MapIcon, 
  Maximize2, 
  Wrench, 
  AlertTriangle,
  Droplets,
  Wind,
  Zap,
  History,
  Eye,
  Camera,
  Video,
  Database,
  Shield,
  Battery,
  Wifi,
  Thermometer,
  Truck,
  User,
  X,
  Plus,
  ChevronDown,
  ChevronUp,
  Ticket,
  CheckSquare,
  Sparkles,
  MessageSquare,
  Send,
  UserPlus,
  FileText,
  Navigation
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../ui/utils';

// Import the full-featured standalone screens
import TankMonitor from './TankMonitor';
import SurveillanceHub from './SurveillanceHub';

// -----------------------------------------------------------------------------
// 🌍 CONFIGURATION
// -----------------------------------------------------------------------------
// NOTE: To enable Photorealistic 3D Tiles, you must create a Map ID in Google Cloud Console
// with "Vector Map" and "3D" enabled, and replace this ID.
const MAP_ID = "63c24069909690b498a57fb4"; 

const containerStyle = {
  width: '100%',
  height: '100vh'
};

const defaultCenter = {
  lat: 31.9973,
  lng: -102.0779 // Midland, TX
};

const mapOptions: google.maps.MapOptions = {
  mapId: MAP_ID,
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  tilt: 60,
  heading: 0,
  mapTypeId: 'hybrid', // Force hybrid (satellite + labels)
};

// -----------------------------------------------------------------------------
// 🧠 GENIUS COMPONENTS
// -----------------------------------------------------------------------------

const TankMonitorModal = ({ well, onClose, onCreateTicket, usePortal = true }: { well: any, onClose: () => void, onCreateTicket?: (well: any) => void, usePortal?: boolean }) => {
  const [isClosing, setIsClosing] = useState(false);
  
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 500);
  };

  // Dynamic Tanks Data based on well properties
  const oilTankCount = typeof well.oilTanks === 'number' ? well.oilTanks : 2;
  const waterTankCount = typeof well.waterTanks === 'number' ? well.waterTanks : 1;

  const tanks = [
    ...Array.from({ length: oilTankCount }).map((_, i) => ({
      id: `oil-${i}`,
      type: 'oil',
      name: `Oil Tank ${i + 1}`,
      capacity: 500,
      level: Math.floor(Math.random() * 480), // Mock level
      temp: 90 + Math.floor(Math.random() * 15),
      status: Math.random() > 0.8 ? 'warning' : 'normal'
    })),
    ...Array.from({ length: waterTankCount }).map((_, i) => ({
      id: `water-${i}`,
      type: 'water',
      name: `Water Tank ${i + 1}`,
      capacity: 400,
      level: Math.floor(Math.random() * 380),
      temp: 80 + Math.floor(Math.random() * 10),
      status: 'normal'
    }))
  ];

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-500">
      {/* HUD Grid Background */}
      <div className="absolute inset-0 pointer-events-none opacity-20" 
           style={{ backgroundImage: 'linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
      </div>
      
      <Card className={cn(
        "w-full max-w-4xl bg-slate-950/90 border-2 border-blue-500/50 text-white shadow-[0_0_50px_rgba(59,130,246,0.3)] overflow-hidden transition-all duration-500 relative",
        isClosing ? "animate-out zoom-out-90 fade-out slide-out-to-bottom-10" : "animate-in zoom-in-95 fade-in slide-in-from-bottom-10"
      )}>
        {/* Scanning Line Animation */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-blue-500/5 to-transparent h-[200%] w-full animate-[scan_4s_linear_infinite]"></div>

        {/* Header */}
        <div className="p-4 border-b border-blue-900/50 flex justify-between items-center bg-slate-900/50 relative z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg border border-blue-500/30 shadow-[0_0_10px_rgba(59,130,246,0.5)]">
              <Database className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <h3 className="font-bold text-xl tracking-tight text-blue-100 drop-shadow-md">TANK FARM MONITOR</h3>
              <p className="text-xs text-blue-400 font-mono uppercase tracking-wider">{well.wellName || well.name} • TANK BATTERY A</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              className="bg-yellow-600/80 hover:bg-yellow-500 text-white border border-yellow-400/50 backdrop-blur-sm"
              onClick={() => {
                toast.success("Hauling ticket created");
                onCreateTicket?.(well);
              }}
            >
              <Truck className="h-4 w-4 mr-2" />
              Request Haul
            </Button>
            <Button variant="ghost" size="icon" onClick={handleClose} className="text-slate-400 hover:text-white hover:bg-slate-800 rounded-full border border-transparent hover:border-white/20">
              <X className="h-6 w-6" />
            </Button>
          </div>
        </div>
        
        <div className="p-6 bg-gradient-to-b from-slate-950 to-slate-900 relative z-10">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-slate-900/50 border border-blue-900/30 rounded-xl p-4 flex items-center gap-4 backdrop-blur-sm">
              <div className="p-3 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                <Droplets className="h-6 w-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase font-bold">Total Oil</p>
                <p className="text-2xl font-bold text-white font-mono">1,250 <span className="text-sm text-slate-500 font-normal">bbl</span></p>
              </div>
            </div>
            <div className="bg-slate-900/50 border border-blue-900/30 rounded-xl p-4 flex items-center gap-4 backdrop-blur-sm">
              <div className="p-3 bg-blue-500/10 rounded-full border border-blue-500/20">
                <Droplets className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase font-bold">Total Water</p>
                <p className="text-2xl font-bold text-white font-mono">530 <span className="text-sm text-slate-500 font-normal">bbl</span></p>
              </div>
            </div>
            <div className="bg-slate-900/50 border border-blue-900/30 rounded-xl p-4 flex items-center gap-4 backdrop-blur-sm">
              <div className="p-3 bg-yellow-500/10 rounded-full border border-yellow-500/20">
                <Truck className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase font-bold">Hauls Pending</p>
                <p className="text-2xl font-bold text-white font-mono">2 <span className="text-sm text-slate-500 font-normal">scheduled</span></p>
              </div>
            </div>
            <div className="bg-slate-900/50 border border-blue-900/30 rounded-xl p-4 flex items-center gap-4 backdrop-blur-sm">
              <div className="p-3 bg-purple-500/10 rounded-full border border-purple-500/20">
                <Activity className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase font-bold">Battery Health</p>
                <p className="text-2xl font-bold text-emerald-400 font-mono">98% <span className="text-sm text-slate-500 font-normal">optimal</span></p>
              </div>
            </div>
          </div>

          {/* Tanks Grid */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 h-80">
            {tanks.map((tank) => {
              const fillPercentage = (tank.level / tank.capacity) * 100;
              const isCritical = fillPercentage > 90;
              
              return (
                <div key={tank.id} className="relative group">
                  {/* Tank Visual */}
                  <div className="h-full bg-slate-800/30 rounded-lg border border-slate-700 overflow-hidden relative flex flex-col justify-end shadow-inner backdrop-blur-sm">
                    {/* Liquid */}
                    <div 
                      className={cn(
                        "w-full transition-all duration-1000 ease-in-out relative",
                        tank.type === 'oil' 
                          ? "bg-gradient-to-t from-stone-950 via-stone-800 to-stone-700" 
                          : "bg-gradient-to-t from-blue-950 via-blue-800 to-blue-600"
                      )}
                      style={{ height: `${fillPercentage}%` }}
                    >
                      {/* Surface shimmer */}
                      <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/20"></div>
                    </div>

                    {/* Glass Reflection Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-white/5 via-transparent to-black/20 pointer-events-none"></div>
                    
                    {/* Level Markers */}
                    <div className="absolute right-0 top-0 bottom-0 w-6 flex flex-col justify-between py-2 px-1 pointer-events-none">
                      {[100, 75, 50, 25, 0].map(mark => (
                        <div key={mark} className="w-full h-[1px] bg-slate-600/50"></div>
                      ))}
                    </div>

                    {/* Info Overlay */}
                    <div className="absolute top-2 left-2 right-2">
                      <div className="flex justify-between items-start">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">{tank.name}</span>
                        {isCritical && (
                          <div className="animate-pulse bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-[0_0_10px_rgba(239,68,68,0.5)]">FULL</div>
                        )}
                      </div>
                    </div>

                    {/* Center Stats */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="text-center drop-shadow-md">
                        <p className="text-xl font-bold text-white font-mono">{tank.level}</p>
                        <p className="text-[10px] text-slate-300">bbl</p>
                      </div>
                    </div>
                  </div>

                  {/* Hover / Action State */}
                  {isCritical && (
                    <div className="absolute -bottom-12 left-0 right-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <Button 
                        size="sm" 
                        className="w-full bg-red-600 hover:bg-red-500 text-white text-xs shadow-lg"
                        onClick={() => {
                          toast.info(`Scheduling haul for ${tank.name}`);
                          onCreateTicket?.(well);
                        }}
                      >
                        <Truck className="h-3 w-3 mr-1" />
                        Haul
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Legend / Footer */}
          <div className="mt-6 flex justify-between items-center text-xs text-slate-500 border-t border-slate-800 pt-4">
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-stone-700 rounded-sm border border-stone-500"></div>
                <span>Crude Oil</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-600 rounded-sm border border-blue-400"></div>
                <span>Produced Water</span>
              </div>
            </div>
            <div className="font-mono text-blue-400/70">
              LAST UPDATE: {new Date().toLocaleTimeString()} • SENSOR STATUS: ONLINE
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
  
  if (!usePortal) {
    return modalContent;
  }

  // Always use portal for z-index safety when enabled
  return createPortal(modalContent, document.body);
};

const SurveillanceHubModal = ({ well, onClose, usePortal = true }: { well: any, onClose: () => void, usePortal?: boolean }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [activeCam, setActiveCam] = useState(1);
  const [showAI, setShowAI] = useState(true);
  const [showThermal, setShowThermal] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 500);
  };

  const cameras = [
    { id: 1, name: 'Wellhead Main', status: 'online', events: 12 },
    { id: 2, name: 'Tank Battery A', status: 'online', events: 3 },
    { id: 3, name: 'Gate Entrance', status: 'recording', events: 45 },
    { id: 4, name: 'Separator Skid', status: 'offline', events: 0 },
  ];

  const events = [
    { id: 1, time: '10:42:15', type: 'person', message: 'Person Detected', confidence: 98, alert: false },
    { id: 2, time: '10:42:18', type: 'safety', message: 'PPE Violation: No Hardhat', confidence: 92, alert: true },
    { id: 3, time: '10:45:00', type: 'vehicle', message: 'Vehicle: Ford F-150', confidence: 99, alert: false },
    { id: 4, time: '11:12:30', type: 'motion', message: 'Motion at Gate', confidence: 85, alert: false },
  ];

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-500">
      {/* HUD Grid Background */}
      <div className="absolute inset-0 pointer-events-none opacity-20" 
           style={{ backgroundImage: 'linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
      </div>

      <Card className={cn(
        "w-full max-w-6xl h-[85vh] shadow-[0_0_60px_rgba(59,130,246,0.2)] overflow-hidden flex flex-col transition-all duration-500 relative",
        "bg-slate-950/90 border-2 border-blue-500/40 text-white",
        isClosing ? "animate-out zoom-out-90 fade-out slide-out-to-bottom-10" : "animate-in zoom-in-95 fade-in slide-in-from-bottom-10"
      )}>
        {/* Corner Accents */}
        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-blue-400 z-20"></div>
        <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-blue-400 z-20"></div>
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-blue-400 z-20"></div>
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-blue-400 z-20"></div>

        {/* Header */}
        <div className="p-4 border-b border-blue-900/50 flex justify-between items-center bg-slate-900/50 relative z-10">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1 bg-red-900/20 border border-red-500/50 rounded text-red-400 text-xs font-bold animate-pulse">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              LIVE FEED
            </div>
            <div>
              <h3 className="font-bold text-lg tracking-wide flex items-center gap-2 text-blue-100 drop-shadow-sm">
                <Eye className="h-5 w-5 text-blue-400" />
                SURVEILLANCE HUB
              </h3>
              <p className="text-xs text-blue-400/70 font-mono uppercase tracking-wider">AI-POWERED SENTRY SYSTEM • {well.wellName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              className={cn(
                "text-xs font-medium transition-colors border-blue-500/30",
                showAI 
                  ? "bg-blue-600/20 text-blue-300 border-blue-400/50 shadow-[0_0_10px_rgba(59,130,246,0.3)]" 
                  : "text-slate-400 hover:bg-slate-800"
              )}
              onClick={() => setShowAI(!showAI)}
            >
              <Sparkles className="h-3 w-3 mr-2" />
              AI OVERLAY
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className={cn(
                "text-xs font-medium transition-colors border-orange-500/30",
                showThermal 
                  ? "bg-orange-600/20 text-orange-300 border-orange-400/50 shadow-[0_0_10px_rgba(249,115,22,0.3)]" 
                  : "text-slate-400 hover:bg-slate-800"
              )}
              onClick={() => setShowThermal(!showThermal)}
            >
              <Thermometer className="h-3 w-3 mr-2" />
              THERMAL
            </Button>
            <div className="h-6 w-[1px] bg-slate-700 mx-2"></div>
            <Button variant="ghost" size="icon" onClick={handleClose} className="text-slate-400 hover:text-white hover:bg-slate-800 rounded-full">
              <X className="h-6 w-6" />
            </Button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden relative z-10">
          {/* Sidebar - Cameras */}
          <div className="w-64 bg-slate-950/50 border-r border-blue-900/30 flex flex-col backdrop-blur-sm">
            <div className="p-3 text-xs font-bold text-blue-400 uppercase tracking-wider border-b border-blue-900/30">Cameras</div>
            <div className="flex-1 overflow-y-auto space-y-1 p-2">
              {cameras.map(cam => (
                <button
                  key={cam.id}
                  onClick={() => setActiveCam(cam.id)}
                  className={cn(
                    "w-full text-left p-3 rounded-lg border transition-all flex items-center justify-between group",
                    activeCam === cam.id 
                      ? "bg-blue-600/20 border-blue-500/50 text-white shadow-[0_0_15px_rgba(59,130,246,0.2)]" 
                      : "bg-transparent border-transparent text-slate-400 hover:bg-slate-800 hover:text-white"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Video className={cn("h-4 w-4", activeCam === cam.id ? "text-blue-400" : "text-slate-500")} />
                    <div>
                      <div className="text-sm font-bold">{cam.name}</div>
                      <div className="text-[10px] opacity-70 uppercase font-semibold font-mono">{cam.status}</div>
                    </div>
                  </div>
                  {cam.status === 'recording' && (
                    <div className={cn("w-2 h-2 rounded-full animate-pulse", activeCam === cam.id ? "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]" : "bg-red-500")}></div>
                  )}
                </button>
              ))}
            </div>
            
            {/* System Health */}
            <div className="p-4 border-t border-blue-900/30 bg-slate-900/50">
              <div className="space-y-3">
                <div className="flex justify-between text-xs text-slate-400 font-medium font-mono">
                  <span>Storage</span>
                  <span>82% Full</span>
                </div>
                <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 w-[82%] shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
                </div>
                <div className="flex justify-between text-xs text-slate-400 font-medium font-mono">
                  <span>Bandwidth</span>
                  <span>4.2 MB/s</span>
                </div>
                <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 w-[60%] shadow-[0_0_10px_rgba(16,185,129,0.5)]"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Video Feed Area */}
          <div className="flex-1 bg-black relative flex flex-col">
            <div className="flex-1 relative overflow-hidden group">
              {/* Mock Video Placeholder */}
              <div className={cn(
                "absolute inset-0 flex items-center justify-center overflow-hidden transition-colors duration-500",
                showThermal ? "bg-gradient-to-br from-orange-900/20 via-red-900/20 to-yellow-900/20" : "bg-slate-900/50"
              )}>
                {/* Grid Overlay */}
                <div className="absolute inset-0 opacity-20" style={{ 
                  backgroundImage: 'linear-gradient(rgba(0,255,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,0,0.1) 1px, transparent 1px)', 
                  backgroundSize: '50px 50px' 
                }}></div>
                
                {/* Central Text */}
                <div className="text-center z-10">
                  <Video className="h-24 w-24 text-slate-800 mx-auto mb-6 animate-pulse" />
                  <p className="text-red-500 font-mono text-lg tracking-widest font-bold animate-pulse">ESTABLISHING SECURE LINK...</p>
                  <p className="text-blue-400 text-sm mt-2 font-bold font-mono">OilWellX Solar Sentry System v2.4</p>
                </div>

                {/* AI Bounding Boxes */}
                {showAI && (
                  <>
                    <div className="absolute top-1/4 left-1/4 w-48 h-64 border-2 border-emerald-500 rounded bg-emerald-500/10 animate-pulse shadow-[0_0_20px_rgba(16,185,129,0.2)]">
                      <div className="absolute -top-6 left-0 bg-emerald-600 text-white text-[10px] px-2 py-0.5 font-bold flex items-center gap-1 shadow-sm font-mono">
                        <User className="h-3 w-3" /> PERSON (98%)
                      </div>
                      {/* Skeleton overlay simulation */}
                      <div className="absolute top-8 left-1/2 w-[2px] h-32 bg-emerald-500/50 -translate-x-1/2"></div>
                      <div className="absolute top-16 left-4 right-4 h-[2px] bg-emerald-500/50"></div>
                    </div>

                    <div className="absolute bottom-1/3 right-1/3 w-24 h-40 border-2 border-red-500 rounded bg-red-500/10 shadow-[0_0_20px_rgba(239,68,68,0.2)]">
                      <div className="absolute -top-6 left-0 bg-red-600 text-white text-[10px] px-2 py-0.5 font-bold flex items-center gap-1 animate-bounce shadow-sm font-mono">
                        <AlertTriangle className="h-3 w-3" /> NO HARDHAT
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Camera HUD Overlay */}
              <div className="absolute top-4 left-4 font-mono text-xs text-emerald-400 font-bold space-y-1 bg-black/60 p-2 rounded backdrop-blur-sm border border-emerald-500/30 shadow-lg">
                <p className="flex items-center gap-2"><span className="w-2 h-2 bg-red-600 rounded-full animate-pulse shadow-[0_0_5px_red]"></span> REC ● [4K] 30FPS</p>
                <p>ISO 800 • F/2.8 • 24mm</p>
                <p>LAT: 31.9973 N • LNG: 102.0779 W</p>
              </div>
              
              <div className="absolute bottom-4 left-4 font-mono text-xs text-white font-bold bg-black/60 p-2 rounded backdrop-blur-sm border border-white/10 shadow-lg">
                <p>{new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
              </div>
            </div>

            {/* Timeline Scrubber */}
            <div className="h-16 bg-slate-950 border-t border-blue-900/30 flex items-center px-4 gap-4">
              <Button size="icon" variant="ghost" className="text-slate-400 hover:text-white hover:bg-slate-800">
                <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-current border-b-[6px] border-b-transparent ml-1"></div>
              </Button>
              <div className="flex-1 h-8 bg-slate-900 rounded border border-slate-700 relative overflow-hidden cursor-pointer group">
                {/* Time markers */}
                <div className="absolute inset-0 flex justify-between px-2 items-center text-[10px] text-slate-500 font-mono pointer-events-none">
                  <span>10:00</span>
                  <span>10:15</span>
                  <span>10:30</span>
                  <span>10:45</span>
                  <span>11:00</span>
                </div>
                {/* Event ticks */}
                <div className="absolute top-0 bottom-0 left-[20%] w-[2px] bg-yellow-500 shadow-[0_0_5px_yellow]"></div>
                <div className="absolute top-0 bottom-0 left-[45%] w-[2px] bg-red-500 shadow-[0_0_5px_red]"></div>
                <div className="absolute top-0 bottom-0 left-[80%] w-[2px] bg-blue-500 shadow-[0_0_5px_blue]"></div>
                
                {/* Playhead */}
                <div className="absolute top-0 bottom-0 left-[82%] w-[2px] bg-red-600 shadow-[0_0_10px_rgba(220,38,38,0.8)] z-10">
                  <div className="absolute -top-1 -left-1.5 w-4 h-2 bg-red-600 rounded-b-sm"></div>
                </div>
              </div>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400 font-bold">1x</Badge>
                <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400 font-bold">HQ</Badge>
              </div>
            </div>
          </div>

          {/* Right Panel - AI Event Log */}
          <div className="w-80 bg-slate-950/50 border-l border-blue-900/30 flex flex-col backdrop-blur-sm">
            <div className="p-3 border-b border-blue-900/30 bg-slate-900/50 flex justify-between items-center">
              <h4 className="text-xs font-bold text-blue-300 uppercase tracking-wider flex items-center gap-2">
                <Shield className="h-3 w-3 text-blue-400" />
                AI Event Log
              </h4>
              <Badge variant="outline" className="text-[10px] border-blue-900 text-blue-400 bg-blue-900/20">Real-time</Badge>
            </div>
            
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {events.map(event => (
                <div key={event.id} className={cn(
                  "p-3 rounded border transition-colors cursor-pointer hover:shadow-md",
                  event.alert 
                    ? "bg-red-900/20 border-red-800/50 hover:bg-red-900/30" 
                    : "bg-slate-900/50 border-slate-800 hover:bg-slate-800 hover:border-slate-700"
                )}>
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex items-center gap-2">
                      {event.type === 'person' && <User className="h-3 w-3 text-blue-400" />}
                      {event.type === 'safety' && <AlertTriangle className="h-3 w-3 text-red-400" />}
                      {event.type === 'vehicle' && <Truck className="h-3 w-3 text-yellow-400" />}
                      {event.type === 'motion' && <Activity className="h-3 w-3 text-slate-400" />}
                      <span className={cn("text-xs font-bold", event.alert ? "text-red-400" : "text-slate-200")}>
                        {event.message}
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-500 font-mono">{event.time}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <div className="text-[10px] text-slate-500 font-medium">Confidence: {event.confidence}%</div>
                    {event.alert && (
                      <Badge className="bg-red-600 dark:bg-red-500 text-white text-[10px] h-4 px-1">ALERT</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-blue-900/30 bg-slate-900/50">
              <Button className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-[0_0_15px_rgba(37,99,235,0.5)]">
                <FileText className="h-3 w-3 mr-2" />
                EXPORT INCIDENT REPORT
              </Button>
              <Button 
                variant="outline" 
                className="w-full mt-2 border-slate-700 text-slate-400 hover:bg-slate-800 text-xs"
                onClick={handleClose}
              >
                CLOSE CAMERA
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  if (!usePortal) {
    return modalContent;
  }

  // Always use portal for z-index safety
  return createPortal(modalContent, document.body);
};

// -----------------------------------------------------------------------------
// 📋 PRODUCTION LOGS MODAL
// -----------------------------------------------------------------------------
const ProductionLogsModal = ({ well, onClose, usePortal = true }: { well: any, onClose: () => void, usePortal?: boolean }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [selectedRange, setSelectedRange] = useState<'7d' | '30d' | '90d'>('7d');

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 500);
  };

  // Mock production log entries
  const logs = [
    { id: 1, date: '2025-12-09', time: '08:30', pumper: 'John D.', oil: 45.2, water: 12.5, gas: 180, notes: 'Normal operation', status: 'complete' },
    { id: 2, date: '2025-12-08', time: '09:15', pumper: 'John D.', oil: 42.8, water: 14.2, gas: 175, notes: 'Slight pressure drop', status: 'complete' },
    { id: 3, date: '2025-12-07', time: '08:45', pumper: 'Mike S.', oil: 48.1, water: 11.8, gas: 185, notes: '', status: 'complete' },
    { id: 4, date: '2025-12-06', time: '07:30', pumper: 'John D.', oil: 44.5, water: 13.1, gas: 178, notes: 'Replaced gauge', status: 'complete' },
    { id: 5, date: '2025-12-05', time: '08:00', pumper: 'John D.', oil: 46.3, water: 12.9, gas: 182, notes: '', status: 'complete' },
    { id: 6, date: '2025-12-04', time: '09:00', pumper: 'Mike S.', oil: 43.7, water: 15.4, gas: 170, notes: 'High water cut', status: 'flagged' },
  ];

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-500">
      {/* HUD Grid Background */}
      <div className="absolute inset-0 pointer-events-none opacity-20" 
           style={{ backgroundImage: 'linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
      </div>

      <Card className={cn(
        "w-full max-w-4xl h-[80vh] shadow-[0_0_60px_rgba(139,92,246,0.2)] overflow-hidden flex flex-col transition-all duration-500 relative",
        "bg-slate-950/90 border-2 border-purple-500/40 text-white",
        isClosing ? "animate-out zoom-out-90 fade-out slide-out-to-bottom-10" : "animate-in zoom-in-95 fade-in slide-in-from-bottom-10"
      )}>
        {/* Corner Accents */}
        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-purple-400 z-20"></div>
        <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-purple-400 z-20"></div>
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-purple-400 z-20"></div>
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-purple-400 z-20"></div>

        {/* Header */}
        <div className="p-4 border-b border-purple-900/50 flex justify-between items-center bg-slate-900/50 relative z-10">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1 bg-purple-900/20 border border-purple-500/50 rounded text-purple-400 text-xs font-bold">
              <History className="w-3 h-3" />
              PRODUCTION HISTORY
            </div>
            <div>
              <h3 className="font-bold text-lg tracking-wide flex items-center gap-2 text-purple-100 drop-shadow-sm">
                <FileText className="h-5 w-5 text-purple-400" />
                {well.wellName || well.name}
              </h3>
              <p className="text-xs text-purple-400/70 font-mono uppercase tracking-wider">DAILY PRODUCTION LOGS • API: {well.apiNumber || 'N/A'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Range Selector */}
            <div className="flex gap-1 bg-slate-800/50 rounded-lg p-1">
              {(['7d', '30d', '90d'] as const).map((range) => (
                <button
                  key={range}
                  onClick={() => setSelectedRange(range)}
                  className={cn(
                    "px-3 py-1 text-xs font-medium rounded transition-all",
                    selectedRange === range
                      ? "bg-purple-600 text-white"
                      : "text-slate-400 hover:text-white hover:bg-slate-700"
                  )}
                >
                  {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
                </button>
              ))}
            </div>
            <div className="h-6 w-[1px] bg-slate-700 mx-2"></div>
            <Button variant="ghost" size="icon" onClick={handleClose} className="text-slate-400 hover:text-white hover:bg-slate-800 rounded-full">
              <X className="h-6 w-6" />
            </Button>
          </div>
        </div>
        
        <div className="flex-1 p-6 overflow-y-auto bg-gradient-to-b from-slate-950 to-slate-900 relative z-10">
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-900/50 border border-purple-900/30 rounded-xl p-4">
              <p className="text-xs text-slate-400 uppercase font-bold mb-1">Avg Oil</p>
              <p className="text-2xl font-bold text-emerald-400 font-mono">45.1 <span className="text-sm text-slate-500">bbl</span></p>
            </div>
            <div className="bg-slate-900/50 border border-purple-900/30 rounded-xl p-4">
              <p className="text-xs text-slate-400 uppercase font-bold mb-1">Avg Water</p>
              <p className="text-2xl font-bold text-blue-400 font-mono">13.3 <span className="text-sm text-slate-500">bbl</span></p>
            </div>
            <div className="bg-slate-900/50 border border-purple-900/30 rounded-xl p-4">
              <p className="text-xs text-slate-400 uppercase font-bold mb-1">Avg Gas</p>
              <p className="text-2xl font-bold text-yellow-400 font-mono">178 <span className="text-sm text-slate-500">mcf</span></p>
            </div>
            <div className="bg-slate-900/50 border border-purple-900/30 rounded-xl p-4">
              <p className="text-xs text-slate-400 uppercase font-bold mb-1">Water Cut</p>
              <p className="text-2xl font-bold text-orange-400 font-mono">22.8 <span className="text-sm text-slate-500">%</span></p>
            </div>
          </div>

          {/* Logs Table */}
          <div className="bg-slate-900/30 border border-purple-900/30 rounded-xl overflow-hidden">
            <div className="grid grid-cols-7 gap-4 p-3 bg-slate-800/50 border-b border-purple-900/30 text-xs font-bold text-slate-400 uppercase">
              <div>Date</div>
              <div>Time</div>
              <div>Pumper</div>
              <div className="text-right">Oil (bbl)</div>
              <div className="text-right">Water (bbl)</div>
              <div className="text-right">Gas (mcf)</div>
              <div>Notes</div>
            </div>
            <div className="divide-y divide-purple-900/20">
              {logs.map((log) => (
                <div key={log.id} className={cn(
                  "grid grid-cols-7 gap-4 p-3 text-sm hover:bg-purple-900/10 transition-colors",
                  log.status === 'flagged' && "bg-orange-900/10"
                )}>
                  <div className="font-mono text-slate-300">{log.date}</div>
                  <div className="font-mono text-slate-400">{log.time}</div>
                  <div className="text-slate-300">{log.pumper}</div>
                  <div className="text-right font-mono text-emerald-400">{log.oil}</div>
                  <div className="text-right font-mono text-blue-400">{log.water}</div>
                  <div className="text-right font-mono text-yellow-400">{log.gas}</div>
                  <div className="text-slate-400 text-xs truncate" title={log.notes}>
                    {log.status === 'flagged' && <AlertTriangle className="h-3 w-3 text-orange-500 inline mr-1" />}
                    {log.notes || '-'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  if (!usePortal) {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

const SmartWellCard = ({ 
  well, 
  onClose,
  onAddEntry, 
  onCreateTicket,
  onCreateTask,
  onNavigate,
  tickets = [],
  tasks = [],
  onShowTanks, 
  onShowCamera,
  onShowLogs
}: { 
  well: any, 
  onClose: () => void, 
  onAddEntry: () => void,
  onCreateTicket: () => void,
  onCreateTask: () => void,
  onNavigate: () => void,
  tickets?: any[],
  tasks?: any[],
  onShowTanks: () => void,
  onShowCamera: () => void,
  onShowLogs: () => void
}) => {
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null);
  const [expandedTask, setExpandedTask] = useState<string | null>(null);

  return (
    <Card 
      className="w-80 bg-slate-950/90 border-2 border-blue-500/30 text-white shadow-[0_0_30px_rgba(59,130,246,0.3)] animate-in fade-in zoom-in duration-300 backdrop-blur-md relative overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* HUD Background Elements */}
      <div className="absolute inset-0 pointer-events-none opacity-10" 
           style={{ backgroundImage: 'linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
      </div>
      <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-blue-400 z-10"></div>
      <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-blue-400 z-10"></div>
      <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-blue-400 z-10"></div>
      <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-blue-400 z-10"></div>

      <div className="p-4 relative z-10">
        <div className="flex justify-between items-start mb-3 border-b border-blue-900/50 pb-2">
          <div>
            <h3 className="font-bold text-lg text-blue-100 drop-shadow-sm">{well.wellName || well.name}</h3>
            <p className="text-[10px] text-blue-400 font-mono tracking-wider">{well.apiNumber || 'API-000-000'}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge 
              className={cn(
                "uppercase text-[10px] tracking-wider font-bold border shadow-[0_0_10px_rgba(0,0,0,0.5)]",
                well.status === 'active' ? "bg-emerald-900/40 text-emerald-400 border-emerald-500/50" : 
                well.status === 'maintenance' ? "bg-yellow-900/40 text-yellow-400 border-yellow-500/50" :
                "bg-red-900/40 text-red-400 border-red-500/50"
              )}
            >
              <div className={cn("w-1.5 h-1.5 rounded-full mr-1.5 animate-pulse", 
                well.status === 'active' ? "bg-emerald-400" : 
                well.status === 'maintenance' ? "bg-yellow-400" : "bg-red-400"
              )}></div>
              {well.status || 'Unknown'}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-full text-slate-400 hover:text-white hover:bg-slate-800"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
              title="Close well card"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Live Pulse Data */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-slate-900/60 rounded p-2 text-center border border-blue-900/30 hover:border-blue-500/50 transition-colors group">
            <Droplets className="h-4 w-4 text-blue-400 mx-auto mb-1 group-hover:scale-110 transition-transform" />
            <div className="text-sm font-bold text-white font-mono">245</div>
            <div className="text-[9px] text-blue-400/70 font-bold">BBL OIL</div>
          </div>
          <div className="bg-slate-900/60 rounded p-2 text-center border border-blue-900/30 hover:border-purple-500/50 transition-colors group">
            <Wind className="h-4 w-4 text-purple-400 mx-auto mb-1 group-hover:scale-110 transition-transform" />
            <div className="text-sm font-bold text-white font-mono">120</div>
            <div className="text-[9px] text-purple-400/70 font-bold">MCF GAS</div>
          </div>
          <div className="bg-slate-900/60 rounded p-2 text-center border border-blue-900/30 hover:border-yellow-500/50 transition-colors group">
            <Zap className="h-4 w-4 text-yellow-400 mx-auto mb-1 group-hover:scale-110 transition-transform" />
            <div className="text-sm font-bold text-white font-mono">8.2</div>
            <div className="text-[9px] text-yellow-400/70 font-bold">SPM</div>
          </div>
        </div>

        {/* Open Tickets Section */}
        {tickets.length > 0 && (
          <div className="mb-4 space-y-2">
            <div className="flex items-center justify-between text-[10px] text-blue-400 uppercase font-bold tracking-wider border-b border-blue-900/30 pb-1">
              <span className="flex items-center gap-1">
                <Ticket className="h-3 w-3" />
                Open Tickets ({tickets.length})
              </span>
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto pr-1 custom-scrollbar">
              {tickets.map(ticket => (
                <div key={ticket.id} className="bg-slate-900/40 rounded border border-slate-800 overflow-hidden hover:border-blue-500/30 transition-colors">
                  <div 
                    className="p-2 flex items-center justify-between cursor-pointer hover:bg-slate-800/50 transition-colors"
                    onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <div className={cn(
                        "w-1.5 h-1.5 rounded-full shrink-0 shadow-[0_0_5px_currentColor]",
                        ticket.priority === 'critical' ? "bg-red-500 text-red-500" :
                        ticket.priority === 'high' ? "bg-orange-500 text-orange-500" :
                        "bg-blue-500 text-blue-500"
                      )} />
                      <span className="text-xs font-medium truncate text-slate-300">{ticket.title}</span>
                    </div>
                    {expandedTicket === ticket.id ? <ChevronUp className="h-3 w-3 text-blue-400" /> : <ChevronDown className="h-3 w-3 text-slate-500" />}
                  </div>
                  
                  {expandedTicket === ticket.id && (
                    <div className="p-2 bg-slate-950/50 text-[10px] space-y-1 border-t border-slate-800 font-mono">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Status:</span>
                        <span className="text-blue-300 capitalize">{ticket.status.replace('_', ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Vendor:</span>
                        <span className="text-slate-300 truncate max-w-[120px]">{ticket.vendorName || 'Unassigned'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Created:</span>
                        <span className="text-slate-300">{new Date(ticket.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Open Tasks Section */}
        {tasks.length > 0 && (
          <div className="mb-4 space-y-2">
            <div className="flex items-center justify-between text-[10px] text-blue-400 uppercase font-bold tracking-wider border-b border-blue-900/30 pb-1">
              <span className="flex items-center gap-1">
                <CheckSquare className="h-3 w-3" />
                Open Tasks ({tasks.length})
              </span>
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto pr-1 custom-scrollbar">
              {tasks.map(task => (
                <div key={task.id} className="bg-slate-900/40 rounded border border-slate-800 overflow-hidden hover:border-emerald-500/30 transition-colors">
                  <div 
                    className="p-2 flex items-center justify-between cursor-pointer hover:bg-slate-800/50 transition-colors"
                    onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <CheckSquare className="h-3 w-3 text-emerald-400 shrink-0" />
                      <span className="text-xs font-medium truncate text-slate-300">{task.title}</span>
                    </div>
                    {expandedTask === task.id ? <ChevronUp className="h-3 w-3 text-blue-400" /> : <ChevronDown className="h-3 w-3 text-slate-500" />}
                  </div>
                  
                  {expandedTask === task.id && (
                    <div className="p-2 bg-slate-950/50 text-[10px] space-y-1 border-t border-slate-800 font-mono">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Status:</span>
                        <span className="text-emerald-300 capitalize">{task.status.replace('_', ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Assigned:</span>
                        <span className="text-slate-300 truncate max-w-[120px]">{task.assignedToName || 'Unassigned'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Priority:</span>
                        <span className={cn(
                          "capitalize",
                          task.priority === 'urgent' ? "text-red-400" :
                          task.priority === 'high' ? "text-orange-400" :
                          "text-blue-400"
                        )}>{task.priority}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-2 mt-4 pt-2 border-t border-blue-900/30">
          <div className="grid grid-cols-4 gap-2">
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-500 text-white border border-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.4)] text-[10px] font-bold"
              onClick={onNavigate}
            >
              <Navigation className="h-3 w-3 mr-1" />
              NAV
            </Button>
            <Button
              size="sm"
              className="bg-orange-600 hover:bg-orange-500 text-white border border-orange-500 shadow-md text-[10px] font-bold"
              onClick={onCreateTicket}
            >
              <Wrench className="h-3 w-3 mr-1" />
              TICKET
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-500 shadow-md text-[10px] font-bold"
              onClick={onAddEntry}
            >
              <Plus className="h-3 w-3 mr-1" />
              ENTRY
            </Button>
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-500 text-white border border-blue-500 shadow-md text-[10px] font-bold"
              onClick={onCreateTask}
            >
              <CheckSquare className="h-3 w-3 mr-1" />
              TASK
            </Button>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            <Button
              size="sm"
              className="bg-slate-800 hover:bg-slate-700 text-blue-300 border border-blue-500/30 hover:border-blue-400"
              onClick={(e) => {
                e.stopPropagation();
                onShowTanks();
              }}
            >
              <Database className="h-3 w-3 mr-1" />
              <span className="text-[10px]">TANKS</span>
            </Button>
            <Button
              size="sm"
              className="bg-slate-800 hover:bg-slate-700 text-purple-300 border border-purple-500/30 hover:border-purple-400"
              onClick={(e) => {
                e.stopPropagation();
                onShowLogs();
              }}
            >
              <History className="h-3 w-3 mr-1" />
              <span className="text-[10px]">LOGS</span>
            </Button>
            <Button
              size="sm"
              className="bg-slate-800 hover:bg-slate-700 text-red-300 border border-red-500/30 hover:border-red-400 animate-pulse"
              onClick={(e) => {
                e.stopPropagation();
                onShowCamera();
              }}
            >
              <Camera className="h-3 w-3 mr-1" />
              <span className="text-[10px]">CAMS</span>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

const EmployeeCard = ({ 
  employee, 
  onClose, 
  onMessage, 
  onAssignTask 
}: { 
  employee: any, 
  onClose: () => void, 
  onMessage: () => void, 
  onAssignTask: () => void 
}) => {
  return (
    <Card className="w-72 bg-slate-950/90 backdrop-blur-md border-2 border-blue-500/30 text-white shadow-[0_0_30px_rgba(59,130,246,0.3)] animate-in fade-in zoom-in duration-300 relative overflow-hidden">
      {/* HUD Background Elements */}
      <div className="absolute inset-0 pointer-events-none opacity-10" 
           style={{ backgroundImage: 'linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
      </div>
      
      <div className="p-4 relative z-10">
        <div className="flex justify-between items-start mb-3 border-b border-blue-900/50 pb-2">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-300 font-bold border border-blue-500/50 shadow-[0_0_10px_rgba(59,130,246,0.3)]">
              {employee.name?.charAt(0) || 'U'}
            </div>
            <div>
              <h3 className="font-bold text-base text-blue-100 drop-shadow-sm">{employee.name}</h3>
              <p className="text-[10px] text-blue-400 font-mono uppercase tracking-wider">{employee.role || 'Field Worker'}</p>
            </div>
          </div>
          <Badge className="bg-emerald-900/40 text-emerald-400 border border-emerald-500/50 text-[10px] shadow-[0_0_5px_rgba(16,185,129,0.3)]">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-1.5 animate-pulse"></div>
            ONLINE
          </Badge>
        </div>

        <div className="space-y-2 mb-4 bg-slate-900/50 p-2 rounded border border-blue-900/30">
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <MapIcon className="h-3 w-3 text-blue-500" />
            <span className="font-mono text-[10px] uppercase tracking-wide">LOC: {employee.nearestWell || 'Unknown Location'}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <Clock className="h-3 w-3 text-blue-500" />
            <span className="font-mono text-[10px] uppercase tracking-wide">ACT: 12m ago</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button 
            size="sm" 
            className="bg-blue-600/80 hover:bg-blue-500 text-white border border-blue-500/50 shadow-[0_0_10px_rgba(37,99,235,0.3)] text-[10px] font-bold"
            onClick={onMessage}
          >
            <MessageSquare className="h-3 w-3 mr-2" />
            MESSAGE
          </Button>
          <Button 
            size="sm" 
            className="bg-indigo-600/80 hover:bg-indigo-500 text-white border border-indigo-500/50 shadow-[0_0_10px_rgba(79,70,229,0.3)] text-[10px] font-bold"
            onClick={onAssignTask}
          >
            <CheckSquare className="h-3 w-3 mr-2" />
            ASSIGN
          </Button>
        </div>
      </div>
    </Card>
  );
};

// -----------------------------------------------------------------------------
// 🚀 MAIN COMPONENT
// -----------------------------------------------------------------------------

export default function SatelliteCommand({ 
  wells: propWells, 
  onAddEntry,
  onCreateTicket,
  onCreateTask,
  onNavigate,
  tickets = [],
  tasks = [],
  employees = []
}: { 
  wells?: any[], 
  onAddEntry?: (well: any) => void,
  onCreateTicket?: (well: any) => void,
  onCreateTask?: (well: any) => void,
  onNavigate?: (well: any) => void,
  tickets?: any[],
  tasks?: any[],
  employees?: any[]
}) {
  const { user } = useAuth();
  // Use props if available, otherwise fetch (fallback)
  const { wells: fetchedWells } = useWells(user?.id, user);
  const wells = propWells || fetchedWells;
  
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedWell, setSelectedWell] = useState<any | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null);
  const [viewMode, setViewMode] = useState<'orbit' | 'drone' | 'xray'>('orbit');
  const [showSubsurface, setShowSubsurface] = useState(false);
  const [timeMachineOpen, setTimeMachineOpen] = useState(false);
  
  // New State for Modals
  const [showTanks, setShowTanks] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [immersiveMode, setImmersiveMode] = useState(false);
  const mobileCardRef = useRef<HTMLDivElement | null>(null);

  // User location state with heading (for direction arrow)
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [userHeading, setUserHeading] = useState<number | null>(null);

  // Watch user location
  useEffect(() => {
    if (!navigator.geolocation) return;
    
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        if (position.coords.heading !== null && !isNaN(position.coords.heading)) {
          setUserHeading(position.coords.heading);
        }
      },
      (error) => console.warn('Location error:', error.message),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );

    // Device orientation for heading (fallback when GPS heading not available)
    const handleOrientation = (event: DeviceOrientationEvent) => {
      if (event.alpha !== null && userHeading === null) {
        // Alpha is compass direction (0-360, 0=north)
        setUserHeading(360 - event.alpha);
      }
    };

    if ('DeviceOrientationEvent' in window) {
      window.addEventListener('deviceorientation', handleOrientation);
    }

    return () => {
      navigator.geolocation.clearWatch(watchId);
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && selectedWell && mobileCardRef.current) {
      mobileCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isMobile, selectedWell]);

  // Mock GPS for employees if missing (Demo Feature)
  const [mappedEmployees, setMappedEmployees] = useState<any[]>([]);

  useEffect(() => {
    if (employees.length > 0 && wells.length > 0) {
      // Assign random locations near wells for demo
      const mapped = employees.map((emp, index) => {
        // Pick a random well to be "near"
        const targetWell = wells[index % wells.length];
        const lat = (targetWell.latitude || targetWell.coordinates?.lat || defaultCenter.lat) + (Math.random() * 0.01 - 0.005);
        const lng = (targetWell.longitude || targetWell.coordinates?.lng || defaultCenter.lng) + (Math.random() * 0.01 - 0.005);
        
        return {
          ...emp,
          latitude: lat,
          longitude: lng,
          nearestWell: targetWell.name
        };
      });
      setMappedEmployees(mapped);
    }
  }, [employees, wells]);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: ["places", "geometry"]
  });

  // 🎮 Camera Controls
  const flyToWell = useCallback((well: any) => {
    if (!map) return;

    const target = {
      lat: well.latitude || well.coordinates?.lat,
      lng: well.longitude || well.coordinates?.lng
    };

    if (!target.lat || !target.lng) return;

    // Cinematic Fly-To Animation (Slower & Realistic)
    const start = {
      lat: map.getCenter()?.lat() || 0,
      lng: map.getCenter()?.lng() || 0,
      zoom: map.getZoom() || 12,
      tilt: map.getTilt() || 0,
      heading: map.getHeading() || 0
    };

    const end = {
      lat: target.lat,
      lng: target.lng,
      zoom: 18,
      tilt: 67.5,
      heading: 45
    };

    const duration = 4000; // 4 seconds for cinematic feel
    const startTime = performance.now();

    const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const animate = (time: number) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeInOutCubic(progress);

      map.moveCamera({
        center: {
          lat: start.lat + (end.lat - start.lat) * eased,
          lng: start.lng + (end.lng - start.lng) * eased
        },
        zoom: start.zoom + (end.zoom - start.zoom) * eased,
        tilt: start.tilt + (end.tilt - start.tilt) * eased,
        heading: start.heading + (end.heading - start.heading) * eased
      });

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);

    setSelectedWell(well);
    setViewMode('drone');
  }, [map]);

  const resetToOrbit = useCallback(() => {
    if (!map) return;
    
    // Calculate bounds of all wells (priority 1)
    const wellsWithCoords = wells.filter(well => well.latitude && well.longitude);
    
    if (wellsWithCoords.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      wellsWithCoords.forEach(well => {
        bounds.extend({ lat: well.latitude, lng: well.longitude });
      });
      
      // Include user location in bounds if nearby (within ~50km of any well)
      if (userLocation) {
        const userNearWells = wellsWithCoords.some(well => {
          const dist = Math.sqrt(
            Math.pow(well.latitude - userLocation.lat, 2) +
            Math.pow(well.longitude - userLocation.lng, 2)
          );
          return dist < 0.5; // ~50km
        });
        if (userNearWells) {
          bounds.extend(userLocation);
        }
      }
      
      map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
      
      // Add a slight delay to set tilt after fitting bounds
      setTimeout(() => {
        map.moveCamera({
          tilt: 60,
          heading: 0
        });
      }, 500);
    } else if (userLocation) {
      // Fallback to user location if no wells (priority 2)
      map.moveCamera({
        center: userLocation,
        zoom: 14,
        tilt: 60,
        heading: 0
      });
    } else {
      // Final fallback to default center (priority 3)
      map.moveCamera({
        center: defaultCenter,
        zoom: 12,
        tilt: 60,
        heading: 0
      });
    }
    
    setSelectedWell(null);
    setViewMode('orbit');
  }, [map, wells, userLocation]);

  const toggleXRay = () => {
    setShowSubsurface(!showSubsurface);
    if (!showSubsurface) {
      map?.setMapTypeId('satellite');
      toast.info("Subsurface X-Ray Enabled");
    } else {
      map?.setMapTypeId('hybrid');
      toast.info("Standard View Restored");
    }
  };

  const toggleImmersiveMode = useCallback(() => {
    setImmersiveMode((prev) => {
      const next = !prev;
      if (next) {
        setSelectedWell(null);
        setSelectedEmployee(null);
        setTimeMachineOpen(false);
        toast.info('Immersive flyover enabled');
      } else {
        toast.success('HUD restored');
      }
      return next;
    });
  }, []);

  // 🔄 Initial Load Effect - Center on wells or user location
  const initialCenterRef = useRef(false);

  useEffect(() => {
    if (map && viewMode === 'orbit' && !initialCenterRef.current) {
      // Only center if we have wells OR if we have user location (and gave wells a chance to load)
      if (wells.length > 0 || userLocation) {
        // Wait a tick for map to be fully ready
        const timer = setTimeout(() => {
          resetToOrbit();
          initialCenterRef.current = true;
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [map, wells.length, userLocation]); // Dependencies are fine, but ref prevents re-running

  // 📱 Mobile View Switching Strategy
  // If a modal is open on mobile, we HIDE the map completely to ensure the modal is visible.
  // This bypasses any z-index or stacking context issues with the map container.
  const isModalOpen = (showTanks || showCamera || showLogs) && !!selectedWell;
  const shouldHideMap = isMobile && isModalOpen;

  useEffect(() => {
    if (!isMobile) return;
    if (typeof document === 'undefined') return;
    if (!isModalOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isModalOpen, isMobile]);

  if (loadError) return <div className="text-red-500 p-10">Error loading Maps API</div>;
  if (!isLoaded) return <div className="bg-black text-white h-screen flex items-center justify-center">Initializing Satellite Command...</div>;

  return (
    <div className="relative h-screen w-full bg-black overflow-hidden">
      
      {/* 🗺️ THE MAP - Hidden when modal is open on mobile */}
      <div className={cn("w-full h-full transition-opacity duration-300", shouldHideMap ? "opacity-0 pointer-events-none absolute inset-0" : "opacity-100")}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={defaultCenter}
          zoom={12}
          onLoad={setMap}
          options={mapOptions}
          onClick={() => setSelectedWell(null)}
        >
          {/* 📍 Well Markers */}
          {wells.map((well) => {
            const position = { 
              lat: well.latitude || well.coordinates?.lat, 
              lng: well.longitude || well.coordinates?.lng 
            };
            
            if (!position.lat || !position.lng) return null;

            const isCritical = well.status === 'critical' || well.hasAlert;

            return (
              <React.Fragment key={well.id}>
                {/* 🚨 Critical Alert Pulse */}
                {isCritical && (
                  <OverlayView
                    position={position}
                    mapPaneName={OverlayView.FLOAT_PANE}
                  >
                    <div className="relative -translate-x-1/2 -translate-y-1/2">
                      <div className="absolute inset-0 w-16 h-16 bg-red-500/30 rounded-full animate-ping"></div>
                      <div className="absolute inset-0 w-16 h-16 bg-red-500/20 rounded-full animate-pulse"></div>
                    </div>
                  </OverlayView>
                )}

                <Marker
                  position={position}
                  onClick={() => {
                    setSelectedWell(well);
                    setSelectedEmployee(null);
                    if (!isMobile) {
                      flyToWell(well);
                    } else {
                      // On mobile, just center the map
                      map?.panTo(position);
                    }
                  }}
                  icon={{
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: isCritical ? 10 : 8,
                    fillColor: isCritical ? '#ef4444' : (well.status === 'active' ? '#22c55e' : '#f59e0b'),
                    fillOpacity: 1,
                    strokeColor: 'white',
                    strokeWeight: 2,
                  }}
                />

                {/* 🦴 X-Ray Subsurface Lines (Simulated) */}
                {showSubsurface && (
                  <Polyline
                    path={[
                      position,
                      { lat: position.lat - 0.002, lng: position.lng + 0.002 } // Simulated lateral
                    ]}
                    options={{
                      strokeColor: well.status === 'active' ? '#22c55e' : '#ef4444',
                      strokeOpacity: 0.6,
                      strokeWeight: 4,
                      geodesic: true,
                      icons: [{
                        icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 2 },
                        offset: '0',
                        repeat: '10px'
                      }]
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}

          {/* 👷 Employee Markers */}
          {mappedEmployees.map((emp) => (
            <Marker
              key={emp.id}
              position={{ lat: emp.latitude, lng: emp.longitude }}
              onClick={() => {
                setSelectedEmployee(emp);
                setSelectedWell(null);
              }}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 6,
                fillColor: '#3b82f6', // Blue for employees
                fillOpacity: 1,
                strokeColor: 'white',
                strokeWeight: 2,
              }}
              label={{
                text: emp.name?.charAt(0) || 'U',
                color: 'white',
                fontSize: '10px',
                fontWeight: 'bold'
              }}
            />
          ))}

          {/* 📍 User Location Marker with Direction Arrow */}
          {userLocation && (
            <>
              {/* Accuracy pulse circle */}
              <Marker
                position={userLocation}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  fillColor: '#3B82F6',
                  fillOpacity: 0.15,
                  strokeColor: '#3B82F6',
                  strokeWeight: 1,
                  scale: 25,
                }}
                zIndex={998}
              />
              
              {/* Direction arrow (shows which way device is pointing) */}
              {userHeading !== null && (
                <Marker
                  position={userLocation}
                  icon={{
                    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    fillColor: '#3B82F6',
                    fillOpacity: 1,
                    strokeColor: '#FFFFFF',
                    strokeWeight: 2,
                    scale: 5,
                    rotation: userHeading,
                    anchor: new google.maps.Point(0, 2.5),
                  }}
                  zIndex={1001}
                />
              )}
              
              {/* Center blue dot */}
              <Marker
                position={userLocation}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  fillColor: '#3B82F6',
                  fillOpacity: 1,
                  strokeColor: '#FFFFFF',
                  strokeWeight: 3,
                  scale: 8,
                }}
                zIndex={1000}
              />
            </>
          )}

          {/* 🃏 Smart Card Overlay */}
          {selectedWell && !isMobile && (
            <OverlayView
              position={{ 
                lat: selectedWell.latitude || selectedWell.coordinates?.lat, 
                lng: selectedWell.longitude || selectedWell.coordinates?.lng 
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div 
                className="absolute bottom-10 left-10"
                style={{ pointerEvents: 'auto' }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <SmartWellCard 
                  well={selectedWell} 
                  onClose={() => setSelectedWell(null)}
                  onAddEntry={() => onAddEntry?.(selectedWell)}
                  onCreateTicket={() => onCreateTicket?.(selectedWell)}
                  onCreateTask={() => onCreateTask?.(selectedWell)}
                  onNavigate={() => {
                    const lat = selectedWell.latitude || selectedWell.coordinates?.lat;
                    const lng = selectedWell.longitude || selectedWell.coordinates?.lng;
                    if (lat && lng) {
                      if (onNavigate) {
                        onNavigate(selectedWell);
                      } else {
                        window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
                      }
                    }
                  }}
                  tickets={tickets.filter(t => t.wellId === selectedWell.id && t.status !== 'resolved' && t.status !== 'closed')}
                  tasks={tasks.filter(t => t.wellId === selectedWell.id && t.status !== 'completed')}
                  onShowTanks={() => setShowTanks(true)}
                  onShowCamera={() => setShowCamera(true)}
                  onShowLogs={() => setShowLogs(true)}
                />
              </div>
            </OverlayView>
          )}



          {/* 👷 Employee Card Overlay */}
          {selectedEmployee && (
            <OverlayView
              position={{ 
                lat: selectedEmployee.latitude, 
                lng: selectedEmployee.longitude 
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div 
                className="absolute bottom-10 left-10"
                style={{ pointerEvents: 'auto' }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <EmployeeCard 
                  employee={selectedEmployee}
                  onClose={() => setSelectedEmployee(null)}
                  onMessage={() => {
                    toast.info(`Opening chat with ${selectedEmployee.name}`);
                    // Future: Open chat modal
                  }}
                  onAssignTask={() => {
                    toast.info(`Assigning task to ${selectedEmployee.name}`);
                    // Future: Open task modal pre-filled with assignee
                  }}
                />
              </div>
            </OverlayView>
          )}
        </GoogleMap>
      </div>

      {/* 📱 Mobile Centered Card - Modal Overlay (restored from v2.1.190) */}
      {selectedWell && isMobile && !isModalOpen && (
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
          onClick={() => setSelectedWell(null)}
        >
          <div
            className="pointer-events-auto animate-in zoom-in-95 duration-200 relative max-w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <SmartWellCard 
              well={selectedWell} 
              onClose={() => setSelectedWell(null)}
              onAddEntry={() => onAddEntry?.(selectedWell)}
              onCreateTicket={() => onCreateTicket?.(selectedWell)}
              onCreateTask={() => onCreateTask?.(selectedWell)}
              onNavigate={() => {
                const lat = selectedWell.latitude || selectedWell.coordinates?.lat;
                const lng = selectedWell.longitude || selectedWell.coordinates?.lng;
                if (lat && lng) {
                  if (onNavigate) {
                    onNavigate(selectedWell);
                  } else {
                    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
                  }
                }
              }}
              tickets={tickets.filter(t => t.wellId === selectedWell.id && t.status !== 'resolved' && t.status !== 'closed')}
              tasks={tasks.filter(t => t.wellId === selectedWell.id && t.status !== 'completed')}
              onShowTanks={() => {
                setShowTanks(true);
              }}
              onShowCamera={() => {
                setShowCamera(true);
              }}
              onShowLogs={() => {
                setShowLogs(true);
              }}
            />
            {/* Close button for mobile - positioned outside card for easier access */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setSelectedWell(null);
              }}
              className="absolute -top-3 -right-3 bg-white text-black rounded-full p-2 shadow-lg z-50 active:scale-95 transition-transform"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* 🎛️ HUD CONTROLS - Hide when modal is open */}
      {!isModalOpen && (
        <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
          <Card className="bg-black/60 backdrop-blur border-white/10 p-2 flex flex-col gap-2">
            <Button 
              variant={viewMode === 'orbit' ? "default" : "ghost"} 
              size="icon"
              onClick={resetToOrbit}
              className={cn("text-white", viewMode === 'orbit' && "bg-blue-600")}
              title="Orbit View"
            >
              <Plane className="h-5 w-5" />
            </Button>
            <Button 
              variant={showSubsurface ? "default" : "ghost"} 
              size="icon"
              onClick={toggleXRay}
              className={cn("text-white", showSubsurface && "bg-purple-600")}
              title="X-Ray Mode"
            >
              <Layers className="h-5 w-5" />
            </Button>
            <Button 
              variant={timeMachineOpen ? "default" : "ghost"} 
              size="icon"
              onClick={() => setTimeMachineOpen(!timeMachineOpen)}
              className={cn("text-white", timeMachineOpen && "bg-yellow-600")}
              title="Time Machine"
            >
              <Clock className="h-5 w-5" />
            </Button>
          </Card>
        </div>
      )}

      {/* 🕰️ TIME MACHINE SLIDER */}
      {timeMachineOpen && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-2/3 max-w-2xl z-10 animate-in slide-in-from-bottom-10">
          <Card className="bg-black/80 backdrop-blur border-white/20 p-4 text-white">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold flex items-center gap-2">
                <History className="h-4 w-4 text-yellow-400" />
                Time Machine
              </h4>
              <span className="text-xs font-mono text-yellow-400">PLAYBACK: NOV 2025</span>
            </div>
            <input 
              type="range" 
              min="0" 
              max="100" 
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-yellow-500" 
            />
            <div className="flex justify-between text-[10px] text-gray-500 mt-1 font-mono">
              <span>30 DAYS AGO</span>
              <span>TODAY</span>
            </div>
          </Card>
        </div>
      )}

      {/* 🛢️ TANK MONITOR - Full Featured Standalone Screen */}
      {showTanks && selectedWell && (
        <div className="fixed inset-0 z-[9999]">
          <TankMonitor 
            well={selectedWell} 
            onClose={() => setShowTanks(false)} 
            onCreateTicket={onCreateTicket}
            isDarkMode={true}
          />
        </div>
      )}

      {/* 📹 SURVEILLANCE HUB - Full Featured Standalone Screen */}
      {showCamera && selectedWell && (
        <div className="fixed inset-0 z-[9999]">
          <SurveillanceHub 
            well={selectedWell} 
            onClose={() => setShowCamera(false)} 
            isDarkMode={true}
          />
        </div>
      )}

      {/* 📋 PRODUCTION LOGS MODAL */}
      {showLogs && selectedWell && (
        <ProductionLogsModal 
          well={selectedWell} 
          onClose={() => setShowLogs(false)} 
          usePortal={!isMobile}
        />
      )}
    </div>
  );
}
